import {
  PipelineItem,
  PipelineType,
  VisaClassType,
  CaseManagerType,
} from '../types'
import {
  subDays,
  subMonths,
  addDays,
  startOfWeek,
  endOfWeek,
  eachWeekOfInterval,
  format,
  differenceInWeeks,
} from 'date-fns'
// Helper function to generate a random date within a range with bias toward more recent dates
const randomDate = (start: Date, end: Date, recentBias: number = 0.3): Date => {
  // Bias toward more recent dates to create an increasing trend
  const bias = Math.random() < recentBias ? 0.7 : 0.3
  const randomFactor = Math.pow(Math.random(), bias) // Power function creates bias
  return new Date(
    start.getTime() + randomFactor * (end.getTime() - start.getTime()),
  )
}
// Helper function to maybe return null for some timestamps with decreasing probability over time
const maybeDate = (
  baseDate: Date,
  now: Date,
  nullProbability: number = 0.1,
): Date | null => {
  // Reduce null probability for more recent dates to simulate improvement over time
  const daysFromNow =
    (now.getTime() - baseDate.getTime()) / (1000 * 60 * 60 * 24)
  const adjustedProbability = nullProbability * (1 + daysFromNow / 365)
  return Math.random() > Math.min(adjustedProbability, 0.4) ? baseDate : null
}
// List of visa classes and names for mock data
const visaClasses: VisaClassType[] = [
  'O-1A',
  'EB-1A',
  'TN',
  'H-1B',
  'EB-2 NIW',
  'O-1A RFE',
  'O-1A Agent',
  'E-3',
  'AOS',
  'J-1',
  'EB-2 NIW RFE',
  'EB-1A RFE',
]
const firstNames = [
  'James',
  'Mary',
  'John',
  'Patricia',
  'Robert',
  'Jennifer',
  'Michael',
  'Linda',
  'William',
  'Elizabeth',
  'David',
  'Susan',
  'Richard',
  'Jessica',
  'Joseph',
  'Sarah',
  'Thomas',
  'Karen',
  'Charles',
  'Nancy',
]
const lastNames = [
  'Smith',
  'Johnson',
  'Williams',
  'Jones',
  'Brown',
  'Davis',
  'Miller',
  'Wilson',
  'Moore',
  'Taylor',
  'Anderson',
  'Thomas',
  'Jackson',
  'White',
  'Harris',
  'Martin',
  'Thompson',
  'Garcia',
  'Martinez',
  'Robinson',
]
const caseManagers: CaseManagerType[] = ['Robby', 'Louise']
// Generate mock data with increasing trend
const generateMockData = (): PipelineItem[] => {
  const types: PipelineType[] = ['form', 'letter', 'memo']
  const now = new Date()
  const startDate = new Date(2023, 3, 1) // April 1, 2023
  const result: PipelineItem[] = []
  let idCounter = 1000
  // Get all weeks from start date to now
  const weeks = eachWeekOfInterval(
    {
      start: startDate,
      end: now,
    },
    { weekStartsOn: 1 },
  ) // Start weeks on Monday
  // For each week, generate at least 20 items of each type
  weeks.forEach((weekStart, weekIndex) => {
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 })
    // Calculate a multiplier that increases over time (more recent weeks have more items)
    const totalWeeks = differenceInWeeks(now, startDate)
    const weeksFromStart = differenceInWeeks(weekStart, startDate)
    const growthFactor = 1 + (weeksFromStart / totalWeeks) * 0.5 // 50% growth from start to end
    // Generate items for each type
    types.forEach((type) => {
      // Base count of 20 items per type per week, increasing over time
      const baseCount = 20
      const count = Math.floor(baseCount * growthFactor)
      for (let i = 0; i < count; i++) {
        // Create date within this week
        const createdAt = randomDate(weekStart, weekEnd)
        // Draft date typically 1-7 days after creation, with increasing probability of completion
        const draftMaxDays = 7 * (1 - (weeksFromStart / totalWeeks) * 0.3) // Draft time decreases over time
        const maxDraftDate = new Date(
          Math.min(
            now.getTime(),
            createdAt.getTime() + draftMaxDays * 24 * 60 * 60 * 1000,
          ),
        )
        // Reduce null probability for more recent items to create an improving trend
        const draftNullProbability =
          0.1 * (1 - (weeksFromStart / totalWeeks) * 0.7)
        const firstDraftAt = maybeDate(
          randomDate(createdAt, maxDraftDate),
          now,
          draftNullProbability,
        )
        // Approval date typically 1-10 days after first draft, with increasing probability of completion
        let approvalAt: Date | null = null
        if (firstDraftAt) {
          const approvalMaxDays = 10 * (1 - (weeksFromStart / totalWeeks) * 0.3) // Approval time decreases over time
          const maxApprovalDate = new Date(
            Math.min(
              now.getTime(),
              firstDraftAt.getTime() + approvalMaxDays * 24 * 60 * 60 * 1000,
            ),
          )
          // Reduce null probability for more recent items
          const approvalNullProbability =
            0.15 * (1 - (weeksFromStart / totalWeeks) * 0.7)
          approvalAt = maybeDate(
            randomDate(firstDraftAt, maxApprovalDate),
            now,
            approvalNullProbability,
          )
        }
        // Random beneficiary and other details
        const firstName =
          firstNames[Math.floor(Math.random() * firstNames.length)]
        const lastName = lastNames[Math.floor(Math.random() * lastNames.length)]
        const beneficiary = `${firstName} ${lastName}`
        const visaClass =
          visaClasses[Math.floor(Math.random() * visaClasses.length)]
        const caseManager =
          caseManagers[Math.floor(Math.random() * caseManagers.length)]
        result.push({
          id: `${type.charAt(0).toUpperCase()}${String(idCounter).padStart(4, '0')}`,
          type,
          title: `${type.charAt(0).toUpperCase() + type.slice(1)} ${idCounter}`,
          beneficiary,
          visaClass,
          caseManager,
          createdAt,
          firstDraftAt,
          approvalAt,
        })
        idCounter++
      }
    })
  })
  return result
}
// Generate the mock data
export const mockData: PipelineItem[] = generateMockData()
